import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ReactDOM from "react-dom";
import variables from "../../constants/assets/variables";
import ChevronDown from "sushiweb/Icons/all/ChevronDown";
import CrossCircle from "sushiweb/Icons/all/CrossCircle";
import { FlexContainer } from "../../constants/assets/containers";
import Spinner from "sushiweb/Spinner/Spinner";
import { getPath } from "../../utils";

// Props-
// * placeholder - placeholder value of dropdown
// * options - list of options to show
// * labelKey - if list is array of objects, then key that refers the label
// * valueKey - if list is array of objects, then key that refers the value
// * keyToChange - if handleChange is passed down the props instead of handleSelect then what key to change
// * handleChange - either pass handleSelect and not pass anything or pass handlechange and pass keyToChange
// * labelWidth - width of label
// * showCross - to show cross icon and reset dropdown functionality
// * showPagination - to enable pagination
// * nextPage - for pagination purposes if showPagination is true
// * handleNextpage - function to call to load the next page, will work when showPagination is true
// * extraParams - additional params to be send in handleChange or handleSelect apart from key and val
// * id- dropdown id -> mandatory
// * marginTop - margin top
// * marginBottom - margin bottom

const YOffset = 170;

const PaginatedReactDropdown = (props) => {
  const {
    placeholder,
    options,
    labelKey,
    valueKey,
    marginTop,
    marginBottom,
    labelWidth,
    showCross,
    keyToChange,
    disabled,
    isOptionDisabled, //fn(value of dropdown)
    handleSelect,
    handleChange,
    showPagination,
    nextPage,
    handleNextPage,
    value,
    noLabelSelect,
    extraParams = [], //extra params to send in handlechange and handleselect call
    extraParamsPagination = [], // extra params to send in handleNextPage call
    extraParamsOptionDisable = [], // extra params to send in isOptionDisabled call
    id, //mandatory
    className,
    width,
    optionsBoxId,
    key,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [inputHeight, setInputHeight] = useState(0);
  const [currEleCoord, setCurrEleCoord] = useState({
    X: "",
    Y: "",
    width: "",
  });
  const [optionsList, setOptionsList] = useState([]);

  let inputDiv = document.getElementById(id);
  let optionsBoxDiv = document.getElementById(optionsBoxId);
  let target = document.getElementById("spinLoader");

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    };

    const observer = new IntersectionObserver(async function (
      entries,
      observer
    ) {
      if (entries[0].isIntersecting && typeof handleNextPage === "function") {
        const updatedList = await handleNextPage(optionsList, [
          ...extraParamsPagination,
        ]);
        if (updatedList.length > 0) {
          setOptionsList(updatedList);
        }
      }
    },
    observerOptions);
    if (target) {
      observer.observe(target);
    }
    return () => observer.disconnect(target);
  }, [optionsList]);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleMenuClick = (val) => {
    !noLabelSelect &&
      setSelectedLabel(
        typeof val !== "object" ? val : getPath(val, labelKey, "")
      );
    setSelectedValue(
      typeof val !== "object" ? val : getPath(val, valueKey, "")
    );
    if (handleSelect) {
      handleSelect(typeof val !== "object" ? val : getPath(val, valueKey, ""), [
        ...extraParams,
      ]);
    } else {
      handleChange(
        keyToChange,
        typeof val !== "object" ? val : getPath(val, valueKey, ""),
        [...extraParams]
      );
    }
  };

  const handleCrossClick = (e) => {
    e.stopPropagation();
    setSelectedLabel("");
    setSelectedValue("");
    if (handleSelect) {
      handleSelect("", [...extraParams]);
    } else {
      handleChange(keyToChange, "", [...extraParams]);
    }
  };

  const checkDisable = (value) => {
    let isDisabled = false;
    if (isOptionDisabled) {
      isDisabled = isOptionDisabled(value, [...extraParamsOptionDisable]);
    }
    return isDisabled;
  };

  useEffect(() => {
    if (inputDiv) {
      const coords = inputDiv.getBoundingClientRect();
      const newCoord = { ...currEleCoord };
      newCoord["X"] = coords.x;
      newCoord["Y"] = coords.y + coords.height + YOffset;
      newCoord["width"] = coords.width;
      setCurrEleCoord(newCoord);
      const height = inputDiv.offsetHeight;
      setInputHeight(height);
    }
  }, [inputDiv, id]);

  useEffect(() => {
    setSelectedLabel(value);
  }, [value]);

  useEffect(() => {
    setOptionsList([...options]);
  }, [options.length]);

  useEffect(() => {
    !showPagination && setOptionsList([...options]);
  }, [options, showPagination]);

  return (
    <PaginatedDropdownContainer
      ref={dropdownRef}
      width={width}
      marginTop={marginTop}
      className="pr-10"
    >
      <PaginatedDropdown
        className={`${className} ${
          disabled ? "disable" : "enable"
        } customDropdown`}
        tabIndex="0"
        // onBlur={handleClose}
        marginBottom={marginBottom}
        id={id}
        align="flex-start"
        onClick={handleClick}
        key={key}
      >
        <FlexContainer style={{ width: "100%" }} justify="space-between">
          <PlaceholderText
            inputHeight={inputHeight}
            labelWidth={labelWidth}
            className={selectedLabel && "isLabel"}
          >
            {placeholder}
          </PlaceholderText>
          {selectedLabel && <InputText>{selectedLabel}</InputText>}
          <IconHolder>
            {selectedLabel && showCross && (
              <CrossCircle
                onClick={handleCrossClick}
                className="cross"
                color={"#000"}
              />
            )}
            <ChevronDown
              className={`chevron ${isOpen && "open"}`}
              color={"#000"}
            />
          </IconHolder>
        </FlexContainer>
        <OptionsBox
          className={isOpen && "open"}
          id={optionsBoxId || "optionsBox"}
          currCoords={currEleCoord}
          open={isOpen}
          inputHeight={inputHeight}
        >
          {optionsList.length > 0 ? (
            optionsList.map((option, index) => {
              return (
                <MenuItem
                  id="optionsMenuItems"
                  className={
                    checkDisable(
                      typeof option !== "object"
                        ? option
                        : getPath(option, labelKey, "")
                    )
                      ? "disabled"
                      : "not-disabled"
                  }
                  onClick={() => handleMenuClick(option)}
                >
                  {typeof option !== "object"
                    ? option
                    : getPath(option, labelKey, "")}
                </MenuItem>
              );
            })
          ) : (
            <NoDataState>No results found</NoDataState>
          )}
          {showPagination && nextPage !== -1 ? (
            <LoaderContainer>
              <Loader id="spinLoader">
                <Spinner size="small" />
              </Loader>
            </LoaderContainer>
          ) : (
            ""
          )}
        </OptionsBox>
      </PaginatedDropdown>
    </PaginatedDropdownContainer>
  );
};

PaginatedReactDropdown.defaultProps = {
  placeholder: "Select item",
  options: [],
  labelWidth: "45%",
  showCross: false,
  showPagination: false,
  nextPage: -1,
};

const PaginatedDropdownContainer = styled.div`
  width: ${({ width }) => (width ? width : "100%")};
  margin-top: ${({ marginTop }) => (marginTop ? marginTop : "0px")};
`;

const PaginatedDropdown = styled(FlexContainer)`
  border: 1px solid ${variables.grey300};
  background: ${variables.grey100};
  flex-direction: column;
  border-radius: 8px;
  padding: 14px 18px;
  box-sizing: border-box;
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? marginBottom : "1rem"};
  width: 100%;
  min-width: 100px;
  max-width: 100%;
  cursor: pointer;
  position: relative;

  .chevron {
    transform: rotate(0deg);
    transition: 0.5s all;
    &.open {
      transform: rotate(180deg);
    }
  }
  &.disable {
    pointer-events: none !important;
    opacity: 0.5;
  }
`;

const PlaceholderText = styled.p`
  margin-right: 20px;
  color: ${variables.grey600};
  &.isLabel {
    font-size: 12px !important;
    color: ${variables.grey700};
    transform: translate(-10px, -${(props) => props.inputHeight / 2}px);
    background: ${variables.grey50};
    margin-right: 0px;
    width: ${(props) => (props.labelWidth ? props.labelWidth : "40%")};
    position: absolute;
  }
`;

const InputText = styled.p``;

const IconHolder = styled.div`
  display: flex;
  .cross {
    opacity: 0.5;
    width: 15px;
    height: auto;
    margin-right: 10px;
  }
`;

const OptionsBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  transform: ${(props) =>
    props.open ? `translateY(${props.inputHeight + 2}px)` : "translateY(0px)"};
  transition: 0.5 all;
  width: 100%;
  border: 1px solid ${variables.grey300};
  border-radius: 8px;
  background: #fff;
  min-height: 5rem;
  max-height: 20rem;
  overflow: scroll;
  display: none;
  // position: absolute;
  // width: ${(props) =>
    props.currCoords && props.currCoords.width
      ? props.currCoords.width + "px"
      : "400px"};
  // will-change: transform;
  // top: 0px;
  // left: 0px;
  // transform: ${(props) =>
    props.currCoords && props.currCoords.X
      ? `translate3d(${props.currCoords.X}px, ${props.currCoords.Y}px, 0px)`
      : "translate3d(102px, 388px, 0px)"} ;
  &.open {
    display: block;
  }
  .disabled {
    opacity: 0.3;
    pointer-events: none;
  }
`;

const NoDataState = styled.p`
  text-align: center;
  color: ${variables.grey500};
  margin-top: 30px;
`;

const LoaderContainer = styled.div`
  padding: 5px 16px;
`;

const Loader = styled.div``;

const MenuItem = styled.div`
  padding: 10px 16px;
  &:hover {
    background: ${variables.red50};
    color: red;
  }
  &:not(:last-child) {
    border-bottom: 1px solid ${variables.grey200};
  }
`;

export default PaginatedReactDropdown;
